// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-CRX-87b — "Attach to ENR-…": a NEW prescription of the same drug joins the patient's open, not-expired
// enrollment instead of starting another (pharmacies that combine — SaaS admin → CareRx). Offered on the PA
// feed row and in the enrollment door; the server re-checks everything (same drug, open, not expired, not
// already covered) and audits who attached it and from where.

import { useState } from 'react'
import AppButton from '../../components/ui/AppButton'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import { friendlyError } from '../../api/errors'
import { formatUsDate } from '../../api/format'
import type { CombineTarget } from '../../api/carerxCombine'
import { isRefusedAttach, useAttachRx } from '../../hooks/queries/carerxCombine'

export function attachLabel(target: CombineTarget): string {
  return `Attach to ${target.therapy_ref}`
}

export default function AttachRxButton({ target, rxNumber, drugName, source, onDone, onRefused, disabled }: {
  target: CombineTarget
  rxNumber: string
  drugName?: string | null
  source: 'pa_feed' | 'door'
  /** Called after a successful attach (reload the list / close the door). */
  onDone: () => void
  /** Stress test 2026-10-21: called when the dialog closes after the server REFUSED the attach (409/422) —
   *  the offer was stale, so the caller reloads its list and the dead button goes away. */
  onRefused?: () => void
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [refused, setRefused] = useState(false)
  const attach = useAttachRx(() => { setOpen(false); onDone() })
  const close = () => { setOpen(false); if (refused) { setRefused(false); onRefused?.() } }
  return (
    <>
      <AppButton variant="contained" size="small" disabled={disabled || attach.isPending}
        onClick={() => { setErr(null); setOpen(true) }}>{attachLabel(target)}</AppButton>
      <ConfirmDialog open={open} title={`Attach Rx ${rxNumber} to ${target.therapy_ref}?`}
        confirmLabel="Attach" busyLabel="Attaching…" busy={attach.isPending} error={err}
        body={<>
          <p style={{ margin: 0 }}>
            Rx <b>{rxNumber}</b>{drugName ? <> ({drugName})</> : null} is the same drug as the patient&#39;s open
            enrollment <b>{target.therapy_ref}</b>. It joins that enrollment instead of starting a new one.
          </p>
          <p style={{ margin: '8px 0 0' }}>The enrollment goes back to <b>Clinical Review</b> and its baseline labs are checked again.</p>
          {target.expires_on && (
            <p style={{ margin: '8px 0 0' }}>The enrollment expires {formatUsDate(target.expires_on)}.</p>)}
        </>}
        onCancel={close}
        onConfirm={() => attach.mutate({ therapyId: target.therapy_id, rxNumber, source }, { onError: e => { setErr(friendlyError(e)); if (isRefusedAttach(e)) setRefused(true) } })} />
    </>
  )
}
